import { Now } from "@/Temporal"
import Icon from "@/components/Icon"
import BootstrapButton from "@/components/form/BootstrapButton"
import InstantInput from "@/components/form/InstantInput"
import type { LatLng } from "@/models"
import { faLocationDot } from "@fortawesome/free-solid-svg-icons"
import { computed, ref } from "vue"
import { Required, defineComponent, proxyProp, type ReactiveComponent } from "vue-utils"
import { useIncidentHandler } from "../../IncidentLogHandler"
import IncidentCoordinateInput from "../../components/IncidentCoordinateInput"
import PickLocationFromMap from "./PickLocationFromMap"

const InitialIncidentLocation: ReactiveComponent = () => {
	const handler = useIncidentHandler()
	const showMap = ref(false)

	const incident = proxyProp(() => handler.incident)
	const sortedPositions = computed(() => {
		const positions = [...incident.positions]
		positions.sort((p1, p2) => p1.receivedAt.compareTo(p2.receivedAt))
		return positions
	})

	const changeBuilder = computed(() => {
		const firstPos = sortedPositions.value[0]
		return handler.changeBuilder.at("positions").id(firstPos.id)
	})
	const positionBuilder = computed(() => changeBuilder.value.at("position"))
	const receivedAtBuilder = computed(() => changeBuilder.value.at("receivedAt"))

	function disabled() {
		return handler.readOnly || !handler.isNewIncident
	}

	function setLocation(loc: LatLng) {
		handler.submitChange(positionBuilder.value.setTo(loc))
		handler.submitChange(receivedAtBuilder.value.setTo(Now.instant()))
		showMap.value = false
	}

	return () => (
		<div class="inline-flex flex-col spacing-3">
			<label>
				<Required label="Position Received At" />
				<InstantInput
					value={receivedAtBuilder.value.getValue(incident)}
					setValue={(newInstant) => handler.submitChange(receivedAtBuilder.value.setTo(newInstant))}
					min={incident.incidentTime}
					disabled={disabled()}
					required
				/>
			</label>
			<div class="flex spacing-8 items-center flex-wrap">
				<label class="flex-grow-1">
					<Required label="Incident Latitude" />
					<IncidentCoordinateInput
						type="latitude"
						path={positionBuilder.value.at("latitude")}
						disabled={disabled()}
						required
					/>
				</label>
				<label class="flex-grow-1">
					<Required label="Incident Longitude" />
					<IncidentCoordinateInput
						type="longitude"
						path={positionBuilder.value.at("longitude")}
						disabled={disabled()}
						required
					/>
				</label>
			</div>

			<BootstrapButton color="success" type="button" onClick={() => (showMap.value = true)}>
				<Icon icon={faLocationDot} />
				<span>{disabled() ? "View Location on Map" : "Pick Location From Map"}</span>
			</BootstrapButton>
			{showMap.value && (
				<PickLocationFromMap
					showLatLngInputs={false}
					position={positionBuilder.value.getValue(incident)}
					setLocation={(loc) => setLocation(loc)}
					cancel={() => (showMap.value = false)}
					isReadOnly={disabled()}
				/>
			)}
		</div>
	)
}

export default defineComponent(InitialIncidentLocation)
